/**
 * Polls backend job progress and updates the notification store.
 *
 * Polls every 3s while status is "running", stops when done/failed.
 * Shows sonner toast on completion or failure.
 */

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/apiClient";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { toast } from "sonner";
import { useEffect, useRef } from "react";

interface JobStatusResponse {
	job_id: string;
	job_type: string;
	title: string;
	status: "running" | "done" | "failed";
	total: number;
	completed: number;
	failed: number;
	created_at: string | null;
	finished_at: string | null;
	error: string | null;
	tenant_id: string | null;
}

export function useJobPoller(jobId: string | null) {
	const { updateNotification } = useNotificationStore();
	const notifiedRef = useRef<Set<string>>(new Set());

	const query = useQuery<JobStatusResponse>({
		queryKey: ["job-status", jobId],
		queryFn: async () => {
			const { data } = await apiClient.get<JobStatusResponse>(
				`/internal-pages/job-status/${jobId}`,
			);
			return data;
		},
		refetchInterval: (query) => {
			const data = query.state.data;
			if (data && (data.status === "done" || data.status === "failed")) {
				return false;
			}
			return 3000;
		},
		enabled: !!jobId,
	});

	// Update notification store + show toasts on status changes
	useEffect(() => {
		if (!query.data || !jobId) return;

		const { status, completed, total, failed, error } = query.data;

		updateNotification(jobId, {
			status,
			progress: { completed, total, failed },
		});

		// Show toast only once per terminal status
		if (
			(status === "done" || status === "failed") &&
			!notifiedRef.current.has(jobId)
		) {
			notifiedRef.current.add(jobId);
			if (status === "done") {
				toast.success(
					`Summaries generated: ${completed}/${total}${failed ? ` (${failed} failed)` : ""}`,
				);
			} else {
				toast.error(
					`Summary generation failed: ${error || "Unknown error"}`,
				);
			}
		}
	}, [query.data, jobId, updateNotification]);

	return query;
}
